import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SCAEnrollmentState } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { MangopayConsentIntro } from '#src/components/fiatWallet/CreateFiatWallet/MangopayConsentIntro/index.tsx';
import { MangopayScaIframeDialog } from '#src/components/fiatWallet/CreateFiatWallet/MangopayScaIframeDialog/index.tsx';
import { MangopayScaIntro } from '#src/components/fiatWallet/CreateFiatWallet/MangopayScaIntro/index.tsx';
import { MangopayScaPopupCallback } from '#src/components/wallet/MangopayScaPopupCallback/index.tsx';
import { ORIGIN } from '#src/config.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useMangopayConsentContext } from '#src/contexts/mangopayConsent/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useTimeoutFn } from '#src/hooks/animations/useTimeoutFn.ts';
import { errorCodes, useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useChildWindow } from '#src/hooks/useChildWindow.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { glossary } from '#src/lib/glossary.ts';

import type {
  AskForProxyConsentMutation,
  AskForProxyConsentMutationVariables,
  GetCurrentUserConsentQuery,
  GetCurrentUserConsentQueryVariables,
} from './__generated__/index.graphql.ts';

const ASK_FOR_PROXY_CONSENT_MUTATION = gql`
  mutation AskForProxyConsentMutation($input: askForProxyConsentInput!) {
    askForProxyConsent(input: $input) {
      mangopayConsentUrl
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  AskForProxyConsentMutation,
  AskForProxyConsentMutationVariables
>;

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const GET_CURRENT_USER_CONSENT_QUERY = gql`
  query GetCurrentUserConsentQuery {
    currentUser {
      slug
      mangopayUser {
        id
        consentGiven
      }
    }
  }
` as TypedDocumentNode<
  GetCurrentUserConsentQuery,
  GetCurrentUserConsentQueryVariables
>;

const usePollCurrentUserScaStatus = ({
  onSuccess,
  onFailed,
}: {
  onSuccess: () => void;
  onFailed?: () => void;
}): [boolean, () => void] => {
  const [polling, setPolling] = useState(false);
  const { data } = useQuery(GET_CURRENT_USER_CONSENT_QUERY, {
    pollInterval: polling ? 1000 : 0,
    skip: !polling,
  });

  const startPolling = () => {
    setPolling(true);
  };

  const stopPolling = () => {
    setPolling(false);
    if (data?.currentUser?.mangopayUser?.consentGiven) {
      onSuccess();
    } else {
      onFailed?.();
    }
  };

  useTimeoutFn(stopPolling, polling ? 10000 : null);

  if (data?.currentUser?.mangopayUser?.consentGiven) {
    stopPolling();
  }

  return [polling, startPolling];
};

export const MangopayConsentDialog = () => {
  const { openPopup, close: closeWindow, background } = useChildWindow();
  const { currentUser } = useCurrentUserContext();
  const { showMangopayConsent, setShowMangopayConsent } =
    useMangopayConsentContext() ?? {};
  const { showNotification } = useSnackNotificationContext();
  const {
    flags: { useIframeForScaFlow },
  } = useFeatureFlags();
  const [askForProxyConsent] = useMutation(ASK_FOR_PROXY_CONSENT_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [consentIframeUrl, setConsentIframeUrl] = useState<string | null>(null);

  const isScaEnrolled =
    currentUser?.mangopayUser?.scaEnrollmentState ===
    SCAEnrollmentState.SCA_ENROLLED;

  const onClose = () => {
    closeWindow();
    setShowMangopayConsent?.(false);
  };

  const retrieveConsentFromPopup = (
    consentUrl: string,
    popup: Window | null
  ) => {
    if (popup) {
      popup.location.href = consentUrl;
    } else {
      window.open(consentUrl, '_self');
    }
  };

  const retrieveConsentFromIframe = (consentUrl: string) => {
    setConsentIframeUrl(consentUrl);
  };

  const onClick = async () => {
    // Must open popup synchronously in click handler to avoid popup blockers
    const popup = !useIframeForScaFlow
      ? (openPopup(`${ORIGIN}/mangopay-sca.html`, 'mangopayScaWindow') ?? null)
      : null;

    const result = await askForProxyConsent({
      variables: {
        input: {
          returnUrl: `${ORIGIN}/mangopay-sca.html`,
        },
      },
    });

    if (result.errors.some(e => e.code === errorCodes.consentAlreadyGiven)) {
      onClose();
      return;
    }

    const consentUrl = result.data?.askForProxyConsent?.mangopayConsentUrl;
    if (!consentUrl) return;

    if (useIframeForScaFlow) {
      retrieveConsentFromIframe(consentUrl);
    } else {
      retrieveConsentFromPopup(consentUrl, popup);
    }
  };

  const onPollSuccess = () => {
    onClose();
  };

  const onStatusNotChanged = () => {
    showNotification('consentProxyManagementFailed');
    onClose();
  };

  const [, startPolling] = usePollCurrentUserScaStatus({
    onSuccess: onPollSuccess,
    onFailed: onStatusNotChanged,
  });

  const onScaFlowSuccess = () => {
    startPolling();
  };

  if (!showMangopayConsent) return null;

  return (
    <>
      {!useIframeForScaFlow && (
        <MangopayScaPopupCallback
          onSuccess={onScaFlowSuccess}
          onFailed={() => {
            showNotification('consentProxyManagementFailed');
            onClose();
          }}
        />
      )}
      <MangopayScaIframeDialog
        open={!!useIframeForScaFlow && !!consentIframeUrl}
        url={consentIframeUrl}
        onSuccess={onScaFlowSuccess}
        onFailed={() => {
          showNotification('consentProxyManagementFailed');
          setConsentIframeUrl(null);
        }}
        onClose={() => setConsentIframeUrl(null)}
      />
      <Dialog open={showMangopayConsent} onClose={onClose} maxWidth="sm">
        <Content gap={3}>
          {isScaEnrolled ? <MangopayConsentIntro /> : <MangopayScaIntro />}
          <Button size="medium" onClick={onClick}>
            <FormattedMessage {...glossary.continue} />
          </Button>
        </Content>
        {!useIframeForScaFlow &&
          background({
            children: (
              <LabelL>
                <FormattedMessage defaultMessage="You have been redirected to Mangopay to complete the security step." />
              </LabelL>
            ),
          })}
      </Dialog>
    </>
  );
};
